import { Identifier } from '@app/utils/common';
import { getValueFromPath } from '@app/utils/get-value-from-path';
import { StringKeys } from '@app/utils/string-keys';

/**
 * Compare two objects using a given key.
 *
 * @param sortOrder Key to sort by, can be:
 *              - a string containing a path to the property
 *              - a function that returns a comparable value
 * @param a Object to compare.
 * @param b Object to compare
 */
export function compare<T>(a: T, b: T, sortOrder: SortOrder<T>) {
  let returnValue = 0;

  const normalized = normalizeSortOrder(sortOrder);
  const { defaultValue = undefined, ignoreCase = false, key, order } = normalized;
  const reversed = order === 'asc' ? 1 : -1;

  let valueA: Identifier;
  let valueB: Identifier;

  if (typeof key === 'function') {
    valueA = key(a);
    valueB = key(b);
  } else {
    valueA = getValueFromPath(key, a, defaultValue);
    valueB = getValueFromPath(key, b, defaultValue);
  }

  if (ignoreCase && typeof valueA === 'string' && typeof valueB === 'string') {
    valueA = valueA.toLowerCase();
    valueB = valueB.toLowerCase();
  }

  if (valueA > valueB) {
    returnValue = 1 * reversed;
  } else if (valueA < valueB) {
    returnValue = -1 * reversed;
  } else if (valueA == null && valueB == null) {
    returnValue = 0;
  } else if (valueA == null) {
    returnValue = -1;
  } else if (valueB == null) {
    returnValue = 1;
  }

  return returnValue;
}

/**
 * Possible sort order definition.
 */
export type SortOrder<T> = SortOrderObject<T> | SortOrderArray<T>;

/**
 * Sort options.
 */
export interface SortOrderObject<T> {
  /**
   * This value is used to compare entries when `object[key]` is null or undefined.
   */
  defaultValue?: T;

  /**
   * Ignore the case of the sort value if it is a string.
   */
  ignoreCase?: boolean;

  /**
   * Either the key of the object to be sorted or a function
   * that takes the object and returns a comparable value.
   */
  key: string | ((o: T) => number | string);

  /**
   * Sort order.
   */
  order: 'asc' | 'desc';
}

/**
 * Array with two parameters:
 *  1. Either the key of the object to be sorted or a function
 *     that takes the object and returns a comparable value.
 *  2. The order: 'asc' | 'desc'
 */
export type SortOrderArray<T> = [
  StringKeys<T> | ((o: T) => number | string),
  'asc' | 'desc' | Omit<SortOrderObject<T>, 'key'>,
];

/**
 * Normalizes the given sort order into a sort order object.
 * @param sortOrder Either a {@link SortOrderArray} or a {@link SortOrderObject}.
 * @return A {@link SortOrderObject}.
 */
export function normalizeSortOrder<T>(sortOrder: SortOrder<T>): SortOrderObject<T> {
  if (Array.isArray(sortOrder)) {
    if (typeof sortOrder[1] === 'object') {
      const { defaultValue = undefined, ignoreCase = false, order } = sortOrder[1];

      return {
        defaultValue,
        ignoreCase,
        key: sortOrder[0],
        order,
      };
    }

    return {
      defaultValue: undefined,
      ignoreCase: false,
      key: sortOrder[0],
      order: sortOrder[1],
    };
  }

  return sortOrder;
}

/**
 * Normalizes the given array of possible sort order values to an array of sort order objects.
 * @param sortOrder Array of possible sort order values (can be {@link SortOrderObject} or {@link SortOrderArray}).
 * @return An array of {@link SortOrderObject}s
 */
export function normalizeSortOrders<T>(
  sortOrder: SortOrder<T> | SortOrder<T>[] | null
): SortOrderObject<T>[] {
  if (sortOrder == null) {
    return [];
  }

  if (Array.isArray(sortOrder)) {
    if (isSortOrderArray(sortOrder) === false) {
      return sortOrder.map(normalizeSortOrder);
    } else {
      return [normalizeSortOrder<T>(sortOrder)];
    }
  } else {
    return [sortOrder];
  }
}

/**
 * Check if the given value is a {@link SortOrderArray}.
 * @param value Possibly a {@link SortOrderArray}.
 */
export function isSortOrderArray<T = any>(value: any): value is SortOrderArray<T> {
  return (
    value.length === 2 &&
    (typeof value[0] === 'string' || typeof value[0] === 'function') &&
    (value[1] === 'asc' || value[1] === 'desc')
  );
}
